<template>
  <div class="gl-products-page">

    <!-- Page header -->
    <div class="container-fluid">
      <div class="gl-branch-header">
        <div>
          <div class="gl-branch-breadcrumb">
            <router-link to="/">Home</router-link>
            <span class="gl-bc-sep">/</span>
            <span>Products</span>
          </div>
          <h1 class="gl-branch-title">Products</h1>
        </div>
        <router-link to="/owner/products/create" class="gl-add-btn gl-header-add-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Product
        </router-link>
      </div>
    </div>

    <div class="container-fluid">

      <!-- Stats strip -->
      <div class="gl-stats-strip">
        <div class="gl-stat-chip">
          <span class="gl-stat-num">{{ pagination.total || products.length }}</span>
          <span class="gl-stat-lbl">Total</span>
        </div>
        <div class="gl-stat-divider"></div>
        <div class="gl-stat-chip">
          <span class="gl-stat-num gl-stat-active">{{ products.filter(p => p.is_active).length }}</span>
          <span class="gl-stat-lbl">Active</span>
        </div>
        <div class="gl-stat-divider"></div>
        <div class="gl-stat-chip">
          <span class="gl-stat-num gl-stat-inactive">{{ products.filter(p => !p.is_active).length }}</span>
          <span class="gl-stat-lbl">Inactive</span>
        </div>
        <div class="gl-stat-divider"></div>
        <div class="gl-stat-chip">
          <span class="gl-stat-num" style="color:#6b7280">{{ categories.length }}</span>
          <span class="gl-stat-lbl">Categories</span>
        </div>
        <div class="gl-stat-spacer"></div>
        <div class="gl-view-toggle">
          <button :class="['gl-vt-btn', { active: viewMode === 'cards' }]" @click="viewMode = 'cards'" title="Card view">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="2" y="2" width="9" height="9" rx="2"/><rect x="13" y="2" width="9" height="9" rx="2"/><rect x="2" y="13" width="9" height="9" rx="2"/><rect x="13" y="13" width="9" height="9" rx="2"/></svg>
          </button>
          <button :class="['gl-vt-btn', { active: viewMode === 'table' }]" @click="viewMode = 'table'" title="Table view">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          </button>
        </div>
      </div>

      <!-- Filter bar -->
      <div class="gl-filter-bar">
        <div class="gl-search-wrap">
          <svg class="gl-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            type="text"
            class="gl-search-input"
            v-model="filters.search"
            placeholder="Search products…"
            @input="debouncedSearch"
          >
        </div>
        <select class="gl-filter-select" v-model="filters.category_id" @change="fetchProducts">
          <option value="">All Categories</option>
          <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
        </select>
        <select class="gl-filter-select" v-model="filters.status" @change="fetchProducts">
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <button class="gl-filter-clear" @click="resetFilters" v-if="filters.search || filters.category_id || filters.status">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          Clear
        </button>
      </div>

      <!-- ── SKELETON ── -->
      <template v-if="loading">
        <div class="gl-cards-grid">
          <div v-for="i in 8" :key="i" class="gl-product-card gl-skel-card" :style="`animation-delay:${i*40}ms`">
            <div class="gl-skel-img"></div>
            <div class="gl-skel-body">
              <div class="gl-skel-line w70"></div>
              <div class="gl-skel-line w40"></div>
              <div class="gl-skel-line w50" style="margin-top:8px"></div>
            </div>
            <div class="gl-skel-actions">
              <div class="gl-skel-line gl-skel-btn-flex"></div>
              <div class="gl-skel-line gl-skel-btn-flex"></div>
              <div class="gl-skel-line gl-skel-btn-sq"></div>
            </div>
          </div>
        </div>
      </template>

      <!-- ── CARD VIEW ── -->
      <template v-else-if="viewMode === 'cards'">
        <div v-if="products.length === 0" class="gl-empty">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
          <p>No products found.</p>
          <router-link to="/owner/products/create" class="gl-add-btn" style="font-size:13px;padding:8px 16px">Add your first product</router-link>
        </div>

        <div v-else class="gl-cards-grid">
          <div
            v-for="(product, idx) in products"
            :key="product.id"
            class="gl-product-card"
            :style="`animation-delay:${idx * 40}ms`"
          >
            <!-- Category pill top-right -->
            <div class="gl-prod-category">{{ product.category?.name || 'Uncategorized' }}</div>

            <!-- Image / placeholder -->
            <div class="gl-prod-img-wrap">
              <img v-if="product.image && !imgErrors[product.id]" :src="product.image" class="gl-prod-img" :alt="product.name" @error="imgErrors[product.id] = true">
              <div v-else class="gl-prod-img-placeholder">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
              </div>
            </div>

            <!-- Info -->
            <div class="gl-prod-info">
              <div class="gl-prod-name">{{ product.name }}</div>
              <div class="gl-prod-sku">SKU: {{ product.sku || 'N/A' }}</div>
              <div class="gl-prod-price">RM {{ formatPrice(product.base_price) }}</div>
              <div class="gl-prod-meta">
                <span class="gl-prod-branches">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  {{ product.outlets?.length || 0 }} {{ product.outlets?.length === 1 ? 'branch' : 'branches' }}
                </span>
                <span :class="['gl-status-badge', product.is_active ? 'active' : 'inactive']">
                  <span class="gl-status-dot"></span>
                  {{ product.is_active ? 'Active' : 'Inactive' }}
                </span>
              </div>
            </div>

            <!-- Actions -->
            <div class="gl-bc-actions">
              <router-link :to="`/owner/products/${product.id}/edit`" class="gl-bc-btn-edit" title="Edit">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                Edit
              </router-link>
              <button @click="showOutletSettings(product)" class="gl-bc-btn-settings" title="Branch Settings">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                Branches
              </button>
              <button @click="confirmDelete(product)" class="gl-bc-btn-delete" title="Delete">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
              </button>
            </div>
          </div>
        </div>
      </template>

      <!-- ── TABLE VIEW ── -->
      <template v-else>
        <div class="gl-table-wrap">
          <table class="gl-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Category</th>
                <th>Base Price</th>
                <th>Branches</th>
                <th>Status</th>
                <th class="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="products.length === 0">
                <td colspan="6" class="gl-tbl-empty">
                  No products found. <router-link to="/owner/products/create">Create your first product</router-link>
                </td>
              </tr>
              <tr v-for="product in products" :key="product.id" class="gl-tbl-row">
                <td>
                  <div class="gl-tbl-product">
                    <div class="gl-tbl-prod-img">
                      <img v-if="product.image && !imgErrors[product.id]" :src="product.image" class="gl-prod-img-sm" :alt="product.name" @error="imgErrors[product.id] = true">
                      <div v-else class="gl-prod-placeholder-sm">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                      </div>
                    </div>
                    <div>
                      <div class="gl-tbl-prod-name">{{ product.name }}</div>
                      <div class="gl-tbl-prod-sku">SKU: {{ product.sku || 'N/A' }}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <span class="gl-cat-pill">{{ product.category?.name || 'Uncategorized' }}</span>
                </td>
                <td>
                  <span class="gl-tbl-price">RM {{ formatPrice(product.base_price) }}</span>
                </td>
                <td>
                  <span class="gl-branch-count">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    {{ product.outlets?.length || 0 }}
                  </span>
                </td>
                <td>
                  <span :class="['gl-status-badge', product.is_active ? 'active' : 'inactive']">
                    <span class="gl-status-dot"></span>
                    {{ product.is_active ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td>
                  <div class="gl-tbl-actions">
                    <button @click="showOutletSettings(product)" class="gl-tbl-btn-settings" title="Branch Settings">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>
                    </button>
                    <router-link :to="`/owner/products/${product.id}/edit`" class="gl-tbl-btn-edit" title="Edit">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </router-link>
                    <button @click="confirmDelete(product)" class="gl-tbl-btn-delete" title="Delete">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <!-- Pagination -->
      <div v-if="!loading && pagination.last_page > 1" class="gl-pagination">
        <button
          class="gl-page-btn"
          :disabled="pagination.current_page === 1"
          @click="fetchProducts(pagination.current_page - 1)"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <button
          v-for="page in visiblePages"
          :key="page"
          :class="['gl-page-num', { active: page === pagination.current_page }]"
          @click="fetchProducts(page)"
        >{{ page }}</button>
        <button
          class="gl-page-btn"
          :disabled="pagination.current_page === pagination.last_page"
          @click="fetchProducts(pagination.current_page + 1)"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>

    </div>

    <!-- Mobile FAB -->
    <router-link to="/owner/products/create" class="gl-mobile-fab">
      <span class="gl-fab-inner">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        Add Product
      </span>
    </router-link>

    <!-- ── Delete Modal ── -->
    <div v-if="showDeleteModal" class="gl-modal-backdrop" @click.self="showDeleteModal = false">
      <div class="gl-modal">
        <div class="gl-modal-icon gl-modal-icon-danger">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
        </div>
        <h2 class="gl-modal-title">Delete Product</h2>
        <p class="gl-modal-body">
          Are you sure you want to delete <strong>{{ selectedProduct?.name }}</strong>? This action cannot be undone.
        </p>
        <div class="gl-modal-actions">
          <button class="gl-modal-cancel" @click="showDeleteModal = false">Cancel</button>
          <button class="gl-modal-confirm" @click="deleteProduct" :disabled="deleting">
            <span v-if="deleting" class="spinner-border spinner-border-sm" style="width:13px;height:13px;border-width:2px"></span>
            <span v-else>Delete</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ── Branch Settings Modal ── -->
    <div v-if="showOutletModal" class="gl-modal-backdrop" @click.self="showOutletModal = false">
      <div class="gl-modal gl-outlet-modal">

        <!-- Header -->
        <div class="gl-outlet-modal-header">
          <div class="gl-outlet-modal-title-wrap">
            <div class="gl-outlet-modal-icon">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            </div>
            <div>
              <div class="gl-outlet-modal-title">Branch Settings</div>
              <div class="gl-outlet-modal-sub">
                {{ selectedProduct?.name }}
                <span class="gl-outlet-base-price">Base: RM {{ formatPrice(selectedProduct?.base_price) }}</span>
              </div>
            </div>
          </div>
          <button class="gl-outlet-close" @click="showOutletModal = false">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <!-- Loading -->
        <div v-if="loadingOutlets" class="gl-outlet-loading">
          <div class="spinner-border" style="color:#900C27;width:22px;height:22px;border-width:2.5px"></div>
          <span>Loading branch settings…</span>
        </div>

        <!-- Column headers (shown once) -->
        <div v-else>
          <div class="gl-outlet-col-headers">
            <div class="gl-och-branch">Branch</div>
            <div class="gl-och-price">Price</div>
            <div class="gl-och-stock">Stock</div>
            <div class="gl-och-avail">Available</div>
            <div class="gl-och-action"></div>
          </div>

          <div class="gl-outlet-list">
            <div v-for="outlet in outlets" :key="outlet.id" :class="['gl-outlet-row', { 'gl-outlet-saved': savedOutlet === outlet.id }]">
              <!-- Branch name -->
              <div class="gl-outlet-branch-cell">
                <div class="gl-outlet-avatar">{{ outlet.name.charAt(0).toUpperCase() }}{{ (outlet.name.split(' ')[1] || '').charAt(0).toUpperCase() }}</div>
                <div>
                  <div class="gl-outlet-name">{{ outlet.name }}</div>
                  <div v-if="outletSettings[outlet.id] && parseFloat(outletSettings[outlet.id].price) !== parseFloat(selectedProduct?.base_price)" class="gl-outlet-custom-tag">custom price</div>
                </div>
              </div>

              <!-- Price -->
              <div class="gl-outlet-price-cell">
                <div class="gl-price-wrap">
                  <span class="gl-price-prefix">RM</span>
                  <input type="number" class="gl-outlet-input gl-price-input" v-model="outletSettings[outlet.id].price" step="0.01" min="0" placeholder="0.00">
                </div>
              </div>

              <!-- Stock -->
              <div class="gl-outlet-stock-cell">
                <input type="number" class="gl-outlet-input" v-model="outletSettings[outlet.id].stock" min="0" placeholder="0">
              </div>

              <!-- Available toggle -->
              <div class="gl-outlet-avail-cell">
                <button
                  type="button"
                  :class="['gl-toggle', { on: outletSettings[outlet.id].is_available }]"
                  @click="outletSettings[outlet.id].is_available = !outletSettings[outlet.id].is_available"
                >
                  <span class="gl-toggle-knob"></span>
                </button>
                <span class="gl-avail-label">{{ outletSettings[outlet.id].is_available ? 'Yes' : 'No' }}</span>
              </div>

              <!-- Save -->
              <div class="gl-outlet-action-cell">
                <button
                  :class="['gl-outlet-save', { 'gl-outlet-saved-btn': savedOutlet === outlet.id }]"
                  @click="saveOutletSetting(outlet.id)"
                  :disabled="savingOutlet === outlet.id || savedOutlet === outlet.id"
                >
                  <span v-if="savingOutlet === outlet.id" class="spinner-border spinner-border-sm" style="width:11px;height:11px;border-width:2px"></span>
                  <template v-else-if="savedOutlet === outlet.id">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                    Saved
                  </template>
                  <template v-else>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                    Save
                  </template>
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import { ownerProductApi, ownerCategoryApi, ownerBranchApi } from '@/services/ownerApi';

export default {
  name: 'ProductList',

  data() {
    return {
      loading: true,
      deleting: false,
      loadingOutlets: false,
      savingOutlet: null,
      savedOutlet: null,
      viewMode: 'cards',
      showDeleteModal: false,
      showOutletModal: false,
      imgErrors: {},
      products: [],
      categories: [],
      outlets: [],
      outletSettings: {},
      selectedProduct: null,
      searchTimeout: null,
      filters: {
        search: '',
        category_id: '',
        status: ''
      },
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
      }
    };
  },

  computed: {
    visiblePages() {
      const pages = [];
      const total = this.pagination.last_page;
      const current = this.pagination.current_page;
      const delta = 2;
      for (let i = Math.max(1, current - delta); i <= Math.min(total, current + delta); i++) {
        pages.push(i);
      }
      return pages;
    }
  },

  mounted() {
    this.fetchProducts();
    this.fetchCategories();
    if (window.innerWidth <= 767) this.viewMode = 'cards';
    this._onResize = () => { if (window.innerWidth <= 767) this.viewMode = 'cards'; };
    window.addEventListener('resize', this._onResize);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this._onResize);
  },

  methods: {
    formatPrice(price) {
      return parseFloat(price).toFixed(2);
    },

    debouncedSearch() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.fetchProducts(), 300);
    },

    resetFilters() {
      this.filters = { search: '', category_id: '', status: '' };
      this.fetchProducts();
    },

    async fetchCategories() {
      try {
        const response = await ownerCategoryApi.list({ per_page: 100 });
        this.categories = response.data.data;
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    },

    async fetchProducts(page = 1) {
      try {
        this.loading = true;
        const params = { page, ...this.filters };
        const response = await ownerProductApi.list(params);
        this.products = response.data.data;
        if (response.data.meta) {
          this.pagination = {
            current_page: response.data.meta.current_page,
            last_page: response.data.meta.last_page,
            per_page: response.data.meta.per_page,
            total: response.data.meta.total
          };
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        this.loading = false;
      }
    },

    confirmDelete(product) {
      this.selectedProduct = product;
      this.showDeleteModal = true;
    },

    async deleteProduct() {
      if (!this.selectedProduct) return;
      try {
        this.deleting = true;
        await ownerProductApi.delete(this.selectedProduct.id);
        this.showDeleteModal = false;
        this.fetchProducts(this.pagination.current_page);
      } catch (error) {
        console.error('Failed to delete product:', error);
        alert(error.response?.data?.message || 'Failed to delete product');
      } finally {
        this.deleting = false;
        this.selectedProduct = null;
      }
    },

    async showOutletSettings(product) {
      this.selectedProduct = product;
      this.loadingOutlets = true;
      this.showOutletModal = true;

      try {
        const [productRes, outletsRes] = await Promise.all([
          ownerProductApi.get(product.id),
          ownerBranchApi.list({ per_page: 100 })
        ]);
        this.outlets = outletsRes.data.data;
        const productData = productRes.data.data;
        this.outletSettings = {};
        this.outlets.forEach(outlet => {
          const existing = productData.outlets?.find(o => o.id === outlet.id);
          this.outletSettings[outlet.id] = {
            price: existing?.pivot?.price || productData.base_price,
            stock: existing?.pivot?.stock || 0,
            is_available: existing?.pivot?.is_available ?? true
          };
        });
      } catch (error) {
        console.error('Failed to load outlet settings:', error);
      } finally {
        this.loadingOutlets = false;
      }
    },

    async saveOutletSetting(outletId) {
      try {
        this.savingOutlet = outletId;
        await ownerProductApi.updateOutletSettings(
          this.selectedProduct.id,
          outletId,
          this.outletSettings[outletId]
        );
        this.fetchProducts(this.pagination.current_page);
        this.savedOutlet = outletId;
        setTimeout(() => { if (this.savedOutlet === outletId) this.savedOutlet = null; }, 2000);
      } catch (error) {
        console.error('Failed to save outlet setting:', error);
        alert(error.response?.data?.message || 'Failed to save');
      } finally {
        this.savingOutlet = null;
      }
    }
  }
};
</script>

<style scoped>
/* ── Page ── */
.gl-products-page { padding-bottom: 48px; }

/* ── Header ── */
.gl-branch-header {
  display: flex; align-items: flex-end; justify-content: space-between;
  padding: 28px 0 20px;
}
.gl-branch-breadcrumb {
  display: flex; align-items: center; gap: 6px;
  font-size: 12.5px; color: #9ca3af; margin-bottom: 4px;
}
.gl-branch-breadcrumb a { color: #9ca3af; text-decoration: none; }
.gl-branch-breadcrumb a:hover { color: #900C27; }
.gl-bc-sep { opacity: 0.5; }
.gl-branch-title {
  font-size: 22px; font-weight: 700; color: #111318;
  letter-spacing: -0.4px; margin: 0;
}
.gl-add-btn {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 18px; border-radius: 10px;
  background: #900C27; color: #fff;
  font-size: 13.5px; font-weight: 600; text-decoration: none;
  transition: background 0.15s, transform 0.1s, box-shadow 0.15s;
  box-shadow: 0 2px 8px rgba(144,12,39,0.25);
}
.gl-add-btn:hover {
  background: #6f0a1e; color: #fff; transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(144,12,39,0.30);
}

/* ── Stats strip ── */
.gl-stats-strip {
  display: flex; align-items: center; gap: 14px;
  background: #fff; border: 1.5px solid #f0f1f3;
  border-radius: 14px; padding: 12px 18px;
  margin-bottom: 12px;
}
.gl-stat-chip { display: flex; flex-direction: column; align-items: center; gap: 1px; min-width: 44px; }
.gl-stat-num { font-size: 18px; font-weight: 700; color: #111318; line-height: 1.1; }
.gl-stat-num.gl-stat-active { color: #900C27; }
.gl-stat-num.gl-stat-inactive { color: #9ca3af; }
.gl-stat-lbl { font-size: 10.5px; color: #9ca3af; font-weight: 500; letter-spacing: 0.3px; }
.gl-stat-divider { width: 1px; height: 28px; background: #f0f1f3; flex-shrink: 0; }
.gl-stat-spacer { flex: 1; }
.gl-view-toggle { display: flex; gap: 3px; background: #f3f4f6; border-radius: 8px; padding: 3px; }
.gl-vt-btn {
  width: 28px; height: 28px; border-radius: 6px; border: none;
  background: transparent; color: #9ca3af; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s;
}
.gl-vt-btn.active { background: #fff; color: #900C27; box-shadow: 0 1px 4px rgba(0,0,0,0.10); }
.gl-vt-btn:hover:not(.active) { color: #374151; }

/* ── Filter bar ── */
.gl-filter-bar {
  display: flex; align-items: center; gap: 10px;
  margin-bottom: 16px; flex-wrap: wrap;
}
.gl-search-wrap {
  position: relative; flex: 1; min-width: 180px;
}
.gl-search-icon {
  position: absolute; left: 11px; top: 50%; transform: translateY(-50%);
  color: #9ca3af; pointer-events: none;
}
.gl-search-input {
  width: 100%; height: 38px; border: 1.5px solid #e5e7eb;
  border-radius: 10px; padding: 0 12px 0 34px;
  font-size: 13px; color: #111318; background: #fafafa; outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.gl-search-input:focus { border-color: #900C27; background: #fff; box-shadow: 0 0 0 3px rgba(144,12,39,0.10); }
.gl-search-input::placeholder { color: #c4c8d0; }
.gl-filter-select {
  height: 38px; border: 1.5px solid #e5e7eb; border-radius: 10px;
  padding: 0 32px 0 12px; font-size: 13px; color: #374151;
  background: #fafafa; outline: none; cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 10px center;
  transition: border-color 0.15s;
}
.gl-filter-select:focus { border-color: #900C27; outline: none; }
.gl-filter-clear {
  display: inline-flex; align-items: center; gap: 6px;
  height: 38px; padding: 0 14px; border-radius: 10px;
  border: 1.5px solid #fca5a5; background: #fef2f2;
  color: #ef4444; font-size: 13px; font-weight: 500;
  cursor: pointer; transition: all 0.15s;
}
.gl-filter-clear:hover { background: #fee2e2; border-color: #f87171; }

/* ── Empty state ── */
.gl-empty {
  display: flex; flex-direction: column; align-items: center; gap: 12px;
  padding: 64px 24px; color: #9ca3af;
  background: #fff; border: 1.5px solid #f0f1f3; border-radius: 16px;
}
.gl-empty svg { color: #d1d5db; }
.gl-empty p { font-size: 14px; margin: 0; }

/* ── Card grid ── */
.gl-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

/* ── Product card ── */
.gl-product-card {
  background: #fff; border: 1.5px solid #f0f1f3;
  border-radius: 16px; overflow: hidden;
  display: flex; flex-direction: column;
  transition: box-shadow 0.2s, border-color 0.2s, transform 0.2s;
  animation: gl-card-in 0.35s ease both;
  position: relative;
}
@keyframes gl-card-in {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}
.gl-product-card:hover {
  border-color: rgba(144,12,39,0.25);
  box-shadow: 0 8px 24px rgba(144,12,39,0.10);
  transform: translateY(-2px);
}

/* Category pill */
.gl-prod-category {
  position: absolute; top: 10px; right: 10px; z-index: 2;
  font-size: 10.5px; font-weight: 600;
  padding: 3px 9px; border-radius: 20px;
  background: rgba(255,255,255,0.92); color: #374151;
  border: 1px solid #e5e7eb;
  white-space: nowrap; max-width: 120px;
  overflow: hidden; text-overflow: ellipsis;
  backdrop-filter: blur(4px);
}

/* Product image */
.gl-prod-img-wrap {
  width: 100%; height: 180px; overflow: hidden; flex-shrink: 0;
  background: #f8f9fa;
}
.gl-prod-img { width: 100%; height: 100%; object-fit: cover; }
.gl-prod-img-placeholder {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #f0faf8, #e8f5f2);
  color: rgba(144,12,39,0.35);
}

/* Product info */
.gl-prod-info { padding: 14px 14px 10px; flex: 1; }
.gl-prod-name {
  font-size: 14px; font-weight: 700; color: #111318;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.gl-prod-sku { font-size: 11px; color: #9ca3af; margin-top: 2px; }
.gl-prod-price {
  font-size: 17px; font-weight: 800; color: #900C27;
  margin-top: 8px; letter-spacing: -0.3px;
}
.gl-prod-meta {
  display: flex; align-items: center; justify-content: space-between;
  margin-top: 8px;
}
.gl-prod-branches {
  display: flex; align-items: center; gap: 4px;
  font-size: 11.5px; color: #6b7280; font-weight: 500;
}
.gl-prod-branches svg { color: #9ca3af; }

/* Status badge */
.gl-status-badge {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 11px; font-weight: 600; padding: 3px 8px; border-radius: 20px;
}
.gl-status-badge.active { background: rgba(144,12,39,0.10); color: #900C27; }
.gl-status-badge.inactive { background: #f3f4f6; color: #9ca3af; }
.gl-status-dot {
  width: 5px; height: 5px; border-radius: 50%; background: currentColor; flex-shrink: 0;
}
.gl-status-badge.active .gl-status-dot { animation: gl-dot-pulse 2s ease infinite; }
@keyframes gl-dot-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(144,12,39,0.4); }
  70%  { box-shadow: 0 0 0 5px rgba(144,12,39,0); }
  100% { box-shadow: 0 0 0 0 rgba(144,12,39,0); }
}

/* Card action buttons */
.gl-bc-actions {
  display: flex; gap: 6px; padding: 10px 14px 14px; border-top: 1px solid #f3f4f6;
}
.gl-bc-btn-settings, .gl-bc-btn-edit, .gl-bc-btn-delete {
  display: inline-flex; align-items: center; justify-content: center; gap: 5px;
  height: 30px; border-radius: 7px; font-size: 12px; font-weight: 600;
  cursor: pointer; border: none; text-decoration: none;
  transition: all 0.15s;
}
.gl-bc-btn-settings, .gl-bc-btn-edit { flex: 1; }
.gl-bc-btn-delete { flex: 0 0 30px; width: 30px; }
.gl-bc-btn-settings {
  background: rgba(59,130,246,0.08); color: #3b82f6;
}
.gl-bc-btn-settings:hover { background: rgba(59,130,246,0.15); }
.gl-bc-btn-edit {
  background: rgba(144,12,39,0.08); color: #900C27;
}
.gl-bc-btn-edit:hover { background: rgba(144,12,39,0.15); }
.gl-bc-btn-delete {
  background: rgba(239,68,68,0.07); color: #ef4444;
}
.gl-bc-btn-delete:hover { background: rgba(239,68,68,0.14); }

/* ── Skeleton ── */
.gl-skel-card {
  cursor: default; pointer-events: none;
  animation: gl-card-in 0.35s ease both, gl-shimmer 1.5s ease infinite alternate !important;
}
@keyframes gl-shimmer { from { opacity: 0.6; } to { opacity: 1; } }
.gl-skel-img {
  width: 100%; height: 140px;
  background: linear-gradient(90deg, #f0f1f3 25%, #e8e9eb 50%, #f0f1f3 75%);
  background-size: 200% 100%;
  animation: gl-skel-wave 1.5s ease infinite;
}
@keyframes gl-skel-wave {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.gl-skel-body { padding: 14px; display: flex; flex-direction: column; gap: 8px; }
.gl-skel-actions { display: flex; gap: 6px; padding: 10px 14px 14px; border-top: 1px solid #f3f4f6; }
.gl-skel-btn-flex { flex: 1; height: 30px; border-radius: 7px; margin: 0; }
.gl-skel-btn-sq { flex: 0 0 30px; width: 30px; height: 30px; border-radius: 7px; margin: 0; }
.gl-skel-line {
  height: 10px; border-radius: 6px;
  background: linear-gradient(90deg, #f0f1f3 25%, #e8e9eb 50%, #f0f1f3 75%);
  background-size: 200% 100%;
  animation: gl-skel-wave 1.5s ease infinite;
}
.gl-skel-line.w70 { width: 70%; }
.gl-skel-line.w40 { width: 40%; }
.gl-skel-line.w50 { width: 50%; }

/* ── Table view ── */
.gl-table-wrap {
  background: #fff; border: 1.5px solid #f0f1f3;
  border-radius: 16px; overflow: hidden; margin-bottom: 20px;
}
.gl-table { width: 100%; border-collapse: collapse; }
.gl-table thead tr { border-bottom: 1.5px solid #f0f1f3; }
.gl-table th {
  padding: 12px 16px; font-size: 11px; font-weight: 700;
  color: #9ca3af; text-transform: uppercase; letter-spacing: 0.6px;
  white-space: nowrap; text-align: left;
}
.gl-table th.text-center { text-align: center; }
.gl-tbl-row { border-bottom: 1px solid #f8f9fa; transition: background 0.12s; }
.gl-tbl-row:last-child { border-bottom: none; }
.gl-tbl-row:hover { background: #fafef9; }
.gl-table td { padding: 13px 16px; vertical-align: middle; }

.gl-tbl-product { display: flex; align-items: center; gap: 12px; }
.gl-tbl-prod-img { flex-shrink: 0; }
.gl-prod-img-sm { width: 44px; height: 44px; border-radius: 10px; object-fit: cover; }
.gl-prod-placeholder-sm {
  width: 44px; height: 44px; border-radius: 10px;
  background: linear-gradient(135deg, #f0faf8, #e8f5f2);
  color: rgba(144,12,39,0.4);
  display: flex; align-items: center; justify-content: center;
}
.gl-tbl-prod-name { font-size: 13.5px; font-weight: 600; color: #111318; }
.gl-tbl-prod-sku { font-size: 11.5px; color: #9ca3af; margin-top: 2px; }
.gl-cat-pill {
  font-size: 11.5px; font-weight: 500;
  padding: 3px 10px; border-radius: 20px;
  background: #f3f4f6; color: #6b7280;
}
.gl-tbl-price { font-size: 14px; font-weight: 700; color: #111318; }
.gl-branch-count {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 12px; font-weight: 600; color: #6b7280;
  background: #f3f4f6; padding: 3px 10px; border-radius: 20px;
}
.gl-branch-count svg { color: #9ca3af; }
.gl-tbl-empty { text-align: center; color: #9ca3af; padding: 40px !important; font-size: 14px; }
.gl-tbl-empty a { color: #900C27; font-weight: 600; }

.gl-tbl-actions { display: flex; align-items: center; justify-content: center; gap: 6px; }
.gl-tbl-btn-settings, .gl-tbl-btn-edit, .gl-tbl-btn-delete {
  width: 32px; height: 32px; border-radius: 8px; border: none;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; text-decoration: none; transition: all 0.15s;
}
.gl-tbl-btn-settings { background: rgba(59,130,246,0.08); color: #3b82f6; }
.gl-tbl-btn-settings:hover { background: rgba(59,130,246,0.16); }
.gl-tbl-btn-edit { background: rgba(144,12,39,0.08); color: #900C27; }
.gl-tbl-btn-edit:hover { background: rgba(144,12,39,0.16); }
.gl-tbl-btn-delete { background: rgba(239,68,68,0.07); color: #ef4444; }
.gl-tbl-btn-delete:hover { background: rgba(239,68,68,0.14); }

/* ── Pagination ── */
.gl-pagination { display: flex; align-items: center; justify-content: center; gap: 4px; margin-top: 8px; }
.gl-page-btn {
  width: 34px; height: 34px; border-radius: 8px; border: 1.5px solid #e5e7eb;
  background: #fff; color: #6b7280; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s;
}
.gl-page-btn:hover:not(:disabled) { border-color: #900C27; color: #900C27; }
.gl-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.gl-page-num {
  min-width: 34px; height: 34px; border-radius: 8px; border: 1.5px solid #e5e7eb;
  background: #fff; color: #374151; font-size: 13px; font-weight: 600;
  cursor: pointer; padding: 0 8px; transition: all 0.15s;
}
.gl-page-num:hover { border-color: #900C27; color: #900C27; }
.gl-page-num.active { background: #900C27; border-color: #900C27; color: #fff; }

/* ── Delete Modal ── */
.gl-modal-backdrop {
  position: fixed; inset: 0; background: rgba(0,0,0,0.35);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999; backdrop-filter: blur(4px);
  animation: gl-backdrop-in 0.15s ease;
}
@keyframes gl-backdrop-in { from { opacity: 0; } to { opacity: 1; } }
.gl-modal {
  background: #fff; border-radius: 20px;
  padding: 32px 28px; width: 100%; max-width: 400px;
  margin: 16px; text-align: center;
  animation: gl-modal-in 0.2s cubic-bezier(0.34,1.56,0.64,1);
  box-shadow: 0 20px 60px rgba(0,0,0,0.15);
}
@keyframes gl-modal-in {
  from { opacity: 0; transform: scale(0.92) translateY(8px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}
.gl-modal-icon {
  width: 52px; height: 52px; border-radius: 16px; margin: 0 auto 16px;
  display: flex; align-items: center; justify-content: center;
}
.gl-modal-icon-danger { background: rgba(239,68,68,0.10); color: #ef4444; }
.gl-modal-title { font-size: 17px; font-weight: 700; color: #111318; margin: 0 0 8px; }
.gl-modal-body { font-size: 13.5px; color: #6b7280; margin: 0 0 24px; line-height: 1.5; }
.gl-modal-body strong { color: #111318; }
.gl-modal-actions { display: flex; gap: 10px; }
.gl-modal-cancel {
  flex: 1; height: 40px; border-radius: 10px;
  border: 1.5px solid #e5e7eb; background: #fff;
  color: #6b7280; font-size: 13.5px; font-weight: 600;
  cursor: pointer; transition: all 0.15s;
}
.gl-modal-cancel:hover { background: #f9fafb; border-color: #d1d5db; color: #374151; }
.gl-modal-confirm {
  flex: 1; height: 40px; border-radius: 10px; border: none;
  background: #ef4444; color: #fff;
  font-size: 13.5px; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  transition: background 0.15s;
}
.gl-modal-confirm:hover:not(:disabled) { background: #dc2626; }
.gl-modal-confirm:disabled { opacity: 0.65; cursor: not-allowed; }

/* ── Branch Settings Modal ── */
.gl-outlet-modal {
  max-width: 640px; text-align: left; padding: 0; overflow: hidden;
}
.gl-outlet-modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 22px; border-bottom: 1.5px solid #f0f1f3;
}
.gl-outlet-modal-title-wrap { display: flex; align-items: center; gap: 12px; }
.gl-outlet-modal-icon {
  width: 36px; height: 36px; border-radius: 10px;
  background: rgba(144,12,39,0.10); color: #900C27;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.gl-outlet-modal-title { font-size: 14.5px; font-weight: 700; color: #111318; }
.gl-outlet-modal-sub {
  font-size: 12px; color: #9ca3af; margin-top: 2px;
  display: flex; align-items: center; gap: 8px;
}
.gl-outlet-base-price {
  display: inline-flex; align-items: center;
  font-size: 10.5px; font-weight: 600;
  background: rgba(144,12,39,0.08); color: #900C27;
  padding: 1px 8px; border-radius: 20px;
}
.gl-outlet-close {
  width: 30px; height: 30px; border-radius: 8px; border: none;
  background: #f3f4f6; color: #9ca3af; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s; flex-shrink: 0;
}
.gl-outlet-close:hover { background: #e5e7eb; color: #374151; }

.gl-outlet-loading {
  display: flex; align-items: center; gap: 12px;
  padding: 36px 22px; color: #6b7280; font-size: 13.5px;
}

/* Column headers */
.gl-outlet-col-headers {
  display: grid;
  grid-template-columns: 1fr 160px 82px 96px 84px;
  gap: 0; padding: 10px 22px 8px;
  border-bottom: 1px solid #f0f1f3;
}
.gl-outlet-col-headers > div {
  font-size: 10px; font-weight: 700; color: #b0b8c8;
  text-transform: uppercase; letter-spacing: 0.6px;
}
.gl-och-price, .gl-och-stock, .gl-och-avail, .gl-och-action { text-align: center; }

/* Rows */
.gl-outlet-list { max-height: 380px; overflow-y: auto; padding-bottom: 8px; }
.gl-outlet-row {
  display: grid;
  grid-template-columns: 1fr 160px 82px 96px 84px;
  align-items: center; gap: 0;
  padding: 14px 22px;
  border-bottom: 1px solid #f8f9fa;
  transition: background 0.12s;
}
.gl-outlet-row:last-child { border-bottom: none; }
.gl-outlet-row:hover { background: #fafef9; }
.gl-outlet-saved { background: rgba(144,12,39,0.05) !important; }

/* Branch cell */
.gl-outlet-branch-cell { display: flex; align-items: center; gap: 10px; }
.gl-outlet-avatar {
  width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0;
  background: linear-gradient(135deg, #900C27, #6f0a1e);
  color: #fff; font-size: 10px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.gl-outlet-name { font-size: 13px; font-weight: 600; color: #111318; }
.gl-outlet-custom-tag {
  font-size: 9.5px; font-weight: 600; color: #f59e0b;
  background: rgba(245,158,11,0.10); padding: 1px 6px;
  border-radius: 20px; margin-top: 2px; display: inline-block;
}

/* Input cells */
.gl-outlet-price-cell, .gl-outlet-stock-cell { display: flex; justify-content: center; }
.gl-outlet-avail-cell { display: flex; flex-direction: column; align-items: center; gap: 3px; }
.gl-avail-label { font-size: 9.5px; font-weight: 600; color: #9ca3af; }
.gl-outlet-action-cell { display: flex; justify-content: center; }

.gl-outlet-input {
  height: 36px; border: 1.5px solid #e5e7eb; border-radius: 9px;
  padding: 0 10px; font-size: 13.5px; color: #111318; background: #fafafa;
  outline: none; width: 82px; transition: border-color 0.15s, box-shadow 0.15s;
  text-align: right;
}
.gl-outlet-input:focus {
  border-color: #900C27; background: #fff;
  box-shadow: 0 0 0 3px rgba(144,12,39,0.10);
}
.gl-price-wrap { position: relative; }
.gl-price-prefix {
  position: absolute; left: 10px; top: 50%; transform: translateY(-50%);
  font-size: 11px; font-weight: 600; color: #9ca3af; pointer-events: none;
}
.gl-price-input { width: 130px; padding-left: 30px; padding-right: 10px; text-align: left; }

/* Toggle */
.gl-toggle {
  width: 38px; height: 21px; border-radius: 12px;
  border: none; cursor: pointer; padding: 0;
  background: #e5e7eb; position: relative;
  transition: background 0.2s; flex-shrink: 0;
}
.gl-toggle.on { background: #900C27; }
.gl-toggle-knob {
  position: absolute; top: 2.5px; left: 2.5px;
  width: 16px; height: 16px; border-radius: 50%;
  background: #fff; transition: transform 0.2s;
  box-shadow: 0 1px 4px rgba(0,0,0,0.2); display: block;
}
.gl-toggle.on .gl-toggle-knob { transform: translateX(17px); }

/* ── Mobile FAB ── */
.gl-mobile-fab {
  display: none;
  position: fixed; bottom: 24px; right: 20px; z-index: 1050;
  text-decoration: none;
  animation: gl-fab-in 0.35s cubic-bezier(0.34,1.56,0.64,1) both;
}
@keyframes gl-fab-in { from { opacity: 0; transform: scale(0.7) translateY(20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
.gl-fab-inner {
  display: flex; align-items: center; gap: 8px;
  padding: 13px 20px 13px 16px; font-size: 13.5px; font-weight: 700;
  background: #900C27; color: #fff; border-radius: 100px;
  box-shadow: 0 6px 24px rgba(144,12,39,0.42), 0 2px 6px rgba(0,0,0,0.12);
  transition: transform 0.15s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.15s;
  letter-spacing: 0.1px;
}
.gl-mobile-fab:active .gl-fab-inner { transform: scale(0.94); }
.gl-mobile-fab:hover .gl-fab-inner {
  box-shadow: 0 10px 32px rgba(144,12,39,0.50), 0 2px 8px rgba(0,0,0,0.14);
  transform: translateY(-2px);
}

/* ── Mobile responsive ── */
@media (max-width: 767px) {
  .gl-products-page { padding-bottom: 100px; }

  /* Header */
  .gl-branch-header { padding: 16px 0 12px; }
  .gl-branch-title { font-size: 18px; }
  .gl-header-add-btn { display: none !important; }
  .gl-mobile-fab { display: flex; }

  /* Stats strip */
  .gl-stats-strip { padding: 10px 14px; gap: 10px; margin-bottom: 10px; }
  .gl-stat-num { font-size: 15px; }
  .gl-stat-lbl { font-size: 10px; }
  .gl-stat-chip { min-width: 36px; }
  .gl-view-toggle { display: none !important; }

  /* Filter bar: search full-width row 1, selects side-by-side row 2 */
  .gl-filter-bar { flex-wrap: wrap; gap: 8px; }
  .gl-search-wrap { flex: none; width: 100%; min-width: unset; }
  .gl-filter-select { flex: 1; min-width: 0; }
  .gl-filter-clear { width: 100%; justify-content: center; }

  /* Cards grid: single column */
  .gl-cards-grid { grid-template-columns: 1fr; gap: 10px; }

  /* Product card: compact image */
  .gl-prod-img-wrap { height: 120px; }
  .gl-skel-img { height: 120px; }
  .gl-prod-info { padding: 12px 12px 8px; }
  .gl-prod-name { font-size: 13.5px; }
  .gl-prod-price { font-size: 15px; margin-top: 6px; }

  /* Action buttons: Edit + Branches flex:1, Delete small square */
  .gl-bc-actions { padding: 8px 12px 12px; gap: 7px; }
  .gl-bc-btn-settings, .gl-bc-btn-edit {
    flex: 1 !important; height: 38px !important;
    font-size: 12.5px !important; border-radius: 9px !important;
  }
  .gl-bc-btn-delete {
    flex: 0 0 38px !important; width: 38px !important;
    height: 38px !important; border-radius: 9px !important;
  }

  /* Branch settings modal: full-width, stacked outlet rows */
  .gl-outlet-modal { max-width: calc(100vw - 24px) !important; margin: 12px !important; }
  .gl-outlet-col-headers { display: none !important; }
  .gl-outlet-row {
    grid-template-columns: 1fr 1fr !important;
    gap: 10px 8px !important;
    padding: 14px 16px !important;
  }
  .gl-outlet-branch-cell { grid-column: 1 / -1; }
  .gl-outlet-price-cell { justify-content: flex-start !important; }
  .gl-outlet-stock-cell { justify-content: flex-start !important; }
  .gl-outlet-avail-cell { flex-direction: row !important; align-items: center !important; gap: 8px !important; justify-content: flex-start !important; }
  .gl-avail-label { font-size: 12px !important; }
  .gl-outlet-action-cell { grid-column: 1 / -1; }
  .gl-outlet-save { width: 100% !important; justify-content: center !important; height: 40px !important; }
  .gl-price-input { width: 100% !important; }
  .gl-outlet-input { width: 100% !important; }
  .gl-outlet-list { max-height: 60vh; }

  /* Modal — bottom sheet, clear of navbar */
  .gl-modal-backdrop { padding: 68px 16px 16px !important; align-items: center !important; }
  .gl-modal { border-radius: 16px !important; max-height: calc(100vh - 84px) !important; }
}

/* Save button */
.gl-outlet-save {
  display: inline-flex; align-items: center; gap: 5px;
  height: 36px; padding: 0 14px; border-radius: 9px; border: none;
  background: #900C27; color: #fff;
  font-size: 12px; font-weight: 600; cursor: pointer;
  transition: background 0.15s, transform 0.1s;
  white-space: nowrap;
}
.gl-outlet-save:hover:not(:disabled) { background: #6f0a1e; transform: scale(1.02); }
.gl-outlet-save:disabled { opacity: 0.7; cursor: not-allowed; transform: none; }
.gl-outlet-saved-btn { background: #16a34a !important; }
</style>

<style>
/* ── Dark mode ── */
body.dark-only .gl-branch-title { color: rgba(255,255,255,0.92) !important; }
body.dark-only .gl-branch-breadcrumb,
body.dark-only .gl-branch-breadcrumb a { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-branch-breadcrumb a:hover { color: #900C27 !important; }

body.dark-only .gl-stats-strip { background: #262932 !important; border-color: #323644 !important; }
body.dark-only .gl-stat-num { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-stat-lbl { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-stat-divider { background: #3d4255 !important; }
body.dark-only .gl-view-toggle { background: #323644 !important; }
body.dark-only .gl-vt-btn.active { background: #3d4255 !important; box-shadow: none !important; }
body.dark-only .gl-vt-btn:hover:not(.active) { color: rgba(255,255,255,0.6) !important; }

body.dark-only .gl-filter-bar { background: transparent !important; border-color: transparent !important; }
body.dark-only .gl-search-input { background: #1d1e26 !important; border-color: #3d4255 !important; color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-search-input:focus { background: #1d1e26 !important; border-color: #900C27 !important; box-shadow: 0 0 0 3px rgba(144,12,39,0.15) !important; }
body.dark-only .gl-search-input::placeholder { color: rgba(255,255,255,0.2) !important; }
body.dark-only select.gl-filter-select { background: #1d1e26 url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2020/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E") no-repeat right 10px center !important; border-color: #3d4255 !important; color: rgba(255,255,255,0.75) !important; -webkit-appearance: none !important; appearance: none !important; }
body.dark-only .gl-filter-clear { background: rgba(239,68,68,0.12) !important; border-color: rgba(239,68,68,0.3) !important; }

body.dark-only .gl-product-card { background: #262932 !important; border-color: #323644 !important; }
body.dark-only .gl-product-card:hover { border-color: rgba(144,12,39,0.35) !important; box-shadow: 0 8px 24px rgba(0,0,0,0.3) !important; }
body.dark-only .gl-prod-category { background: rgba(38,41,50,0.9) !important; border-color: #3d4255 !important; color: rgba(255,255,255,0.6) !important; }
body.dark-only .gl-prod-img-placeholder { background: linear-gradient(135deg, #2a3340, #243330) !important; }
body.dark-only .gl-prod-name { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-prod-sku { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-prod-price { color: #4caf97 !important; }
body.dark-only .gl-prod-branches { color: rgba(255,255,255,0.4) !important; }
body.dark-only .gl-status-badge.inactive { background: rgba(255,255,255,0.06) !important; color: rgba(255,255,255,0.35) !important; }
body.dark-only .gl-bc-actions { border-top-color: #323644 !important; }
body.dark-only .gl-bc-btn-settings { background: rgba(59,130,246,0.12) !important; }
body.dark-only .gl-bc-btn-edit { background: rgba(144,12,39,0.12) !important; }
body.dark-only .gl-bc-btn-delete { background: rgba(239,68,68,0.10) !important; }

body.dark-only .gl-table-wrap { background: #262932 !important; border-color: #323644 !important; }
body.dark-only .gl-table th { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-table thead tr { border-bottom-color: #323644 !important; }
body.dark-only .gl-tbl-row { border-bottom-color: #2d3044 !important; }
body.dark-only .gl-tbl-row:hover { background: rgba(144,12,39,0.06) !important; }
body.dark-only .gl-prod-placeholder-sm { background: linear-gradient(135deg, #2a3340, #243330) !important; }
body.dark-only .gl-tbl-prod-name { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-tbl-prod-sku { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-tbl-price { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-cat-pill { background: rgba(255,255,255,0.06) !important; color: rgba(255,255,255,0.5) !important; }
body.dark-only .gl-branch-count { background: rgba(255,255,255,0.06) !important; color: rgba(255,255,255,0.45) !important; }
body.dark-only .gl-tbl-btn-settings { background: rgba(59,130,246,0.12) !important; }
body.dark-only .gl-tbl-btn-edit { background: rgba(144,12,39,0.12) !important; }
body.dark-only .gl-tbl-btn-delete { background: rgba(239,68,68,0.10) !important; }

body.dark-only .gl-page-btn,
body.dark-only .gl-page-num { background: #262932 !important; border-color: #3d4255 !important; color: rgba(255,255,255,0.55) !important; }
body.dark-only .gl-page-num.active { background: #900C27 !important; border-color: #900C27 !important; color: #fff !important; }

body.dark-only .gl-modal { background: #262932 !important; }
body.dark-only .gl-modal-title { color: rgba(255,255,255,0.92) !important; }
body.dark-only .gl-modal-body { color: rgba(255,255,255,0.5) !important; }
body.dark-only .gl-modal-body strong { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-modal-cancel { background: #323644 !important; border-color: #3d4255 !important; color: rgba(255,255,255,0.55) !important; }
body.dark-only .gl-modal-cancel:hover { background: #3d4255 !important; color: rgba(255,255,255,0.75) !important; }

body.dark-only .gl-outlet-modal-header { border-bottom-color: #323644 !important; }
body.dark-only .gl-outlet-modal-title { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-outlet-modal-sub { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-outlet-close { background: #3d4255 !important; color: rgba(255,255,255,0.5) !important; }
body.dark-only .gl-outlet-close:hover { background: #4a5268 !important; color: rgba(255,255,255,0.75) !important; }
body.dark-only .gl-outlet-col-headers > div { color: rgba(255,255,255,0.2) !important; }
body.dark-only .gl-outlet-col-headers { border-bottom-color: #323644 !important; }
body.dark-only .gl-outlet-row { border-bottom-color: #2d3044 !important; }
body.dark-only .gl-outlet-row:hover { background: rgba(144,12,39,0.06) !important; }
body.dark-only .gl-outlet-saved { background: rgba(144,12,39,0.10) !important; }
body.dark-only .gl-outlet-name { color: rgba(255,255,255,0.75) !important; }
body.dark-only .gl-avail-label { color: rgba(255,255,255,0.25) !important; }
body.dark-only .gl-outlet-input { background: rgba(255,255,255,0.05) !important; border-color: #3d4255 !important; color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-outlet-input:focus { border-color: #900C27 !important; background: rgba(255,255,255,0.07) !important; box-shadow: 0 0 0 2px rgba(144,12,39,0.15) !important; }
body.dark-only .gl-price-prefix { color: rgba(255,255,255,0.25) !important; }
body.dark-only .gl-toggle { background: #3d4255 !important; }
body.dark-only .gl-toggle.on { background: #900C27 !important; }

body.dark-only .gl-empty { background: #262932 !important; border-color: #323644 !important; }
body.dark-only .gl-empty p { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-skel-img,
body.dark-only .gl-skel-line { background: linear-gradient(90deg, #2d3044 25%, #323644 50%, #2d3044 75%) !important; background-size: 200% 100% !important; }
body.dark-only .gl-skel-actions { border-top-color: #2d3044 !important; }
.gl-mobile-fab { background: transparent !important; border: none !important; box-shadow: none !important; }
body.dark-only .gl-mobile-fab { background: transparent !important; box-shadow: none !important; }
body.dark-only .gl-fab-inner { box-shadow: 0 6px 24px rgba(144,12,39,0.35), 0 2px 6px rgba(0,0,0,0.30) !important; }
</style>
